<template>
  <div>
    <section class="p-box">
      <ValidationObserver v-slot="{ setFieldValue }" ref="form">
        <form>
          <div class="p-box__body">
            <Loading v-show="loading" />
            <template v-if="dataLoaded">
              <section v-if="providedFeaturesBusinessHoursCrawler" class="p-box">
                <header class="p-box__header">
                  <h3 class="p-box__title">{{ $t('pages.CompaniesLocationStore.businessHoursCrawler.title') }}</h3>
                </header>
                <div class="p-box__body">
                  <div class="c-info">
                    <div class="c-info__icon">
                      <Icon name="iconQuestion" />
                    </div>
                    <div class="c-info__content">
                      <p>{{ $t('pages.CompaniesLocationStore.businessHoursCrawler.info1') }}</p>
                      <p>{{ $t('pages.CompaniesLocationStore.businessHoursCrawler.info2') }}</p>
                    </div>
                  </div>
                  <div>
                    <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                      <input
                        id="checkbox"
                        v-model="storeBusinessHoursCrawlers.enabled"
                        class="c-checkbox__input"
                        type="checkbox"
                      >
                      <label class="c-checkbox__label" for="checkbox">
                        {{ $t('pages.CompaniesLocationStore.businessHoursCrawler.checkBoxLabel') }}
                      </label>
                    </fieldset>
                    <template v-if="storeBusinessHoursCrawlers.enabled">
                      <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                        <label class="c-formLabel">{{ $t('pages.CompaniesLocationStore.businessHoursCrawler.textBoxLabel') }}</label>
                        <ValidationProvider key="storeBusinessHoursCrawlerUrlValidation" v-slot="{ errors, value, handleChange }" v-model="storeBusinessHoursCrawlers.url" name="URL" rules="required|url">
                          <input
                            :value="value"
                            type="text"
                            class="c-textField"
                            :placeholder="$t('labels.placeholder.enter', { value: 'URL' })"
                            @input="handleChange"
                          >
                          <ValidationError :error="errors[0]" />
                        </ValidationProvider>
                      </fieldset>
                      <Info v-if="storeBusinessHoursCrawlers.status === 'failed'" alert>{{ $t('pages.CompaniesLocationStore.businessHoursCrawler.failed') }}</Info>
                    </template>
                  </div>
                </div>
              </section>
              <section v-if="!enabledBusinessHoursCrawler" class="p-box">
                <header class="p-box__header">
                  <h3 class="p-box__title">{{ $t('pages.CompaniesLocationStore.businessHours.regularHours.title') }}</h3>
                </header>
                <div class="p-box__body">
                  <KkInfo class="qualityCheckDescription">
                    {{ $t('messages.qualityCheckDescription') }}
                  </KkInfo>
                  <div class="c-grid">
                    <div class="c-grid__items">
                      <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__10 c-grid__item__l__10">
                        <section v-for="(regularHour, i) in storeRegularHours" :key="'regularHour_' + i" class="p-box">
                          <RegularHourCard hour-type="regular" :regular-hour="regularHour" :index="'regularHour_' + i" :available-option="isFoodBrand" @set-field-value="setFieldValue" />
                        </section>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <KkBox v-if="!enabledBusinessHoursCrawler && !isDocomoPlan" :title="$t('pages.CompaniesLocationStore.businessHours.holidayHours.title')" border class="section">
                <template #content>
                  <KkInfo class="qualityCheckDescription">
                    {{ $t('messages.qualityCheckDescription') }}
                  </KkInfo>
                  <div class="c-grid">
                    <div class="c-grid__items">
                      <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__10 c-grid__item__l__10">
                        <section v-if="storeHolidayRegularHour" class="p-box">
                          <RegularHourCard hour-type="holiday" :regular-hour="storeHolidayRegularHour" index="holidayRegularHour" :available-option="isFoodBrand" @set-field-value="setFieldValue" />
                        </section>
                        <section v-if="storeDayBeforeHolidayRegularHour" class="p-box">
                          <RegularHourCard hour-type="dayBeforeHoliday" :regular-hour="storeDayBeforeHolidayRegularHour" index="dayBeforeHolidayRegularHour" :available-option="isFoodBrand" @set-field-value="setFieldValue" />
                        </section>
                      </div>
                    </div>
                  </div>
                </template>
              </KkBox>
              <KkBox v-if="!isDocomoPlan" :title="$t('pages.CompaniesLocationStore.businessHours.wdayIndexRegularHours.title')" border class="section">
                <template #content>
                  <KkInfo class="qualityCheckDescription">
                    {{ $t('messages.qualityCheckDescription') }}
                  </KkInfo>
                  <div class="c-grid">
                    <div class="c-grid__items">
                      <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__10 c-grid__item__l__10">
                        <section v-for="(wdayIndexRegularHour, i) in storeWdayIndexRegularHours" :key="'wdayIndexRegularHour_' + i" class="p-box">
                          <KkWdayIndexRegularHourCard
                            :business-hour="wdayIndexRegularHour"
                            :index="'wdayIndexRegularHour_' + i"
                            :can-remove="storeWdayIndexRegularHours.length > 1"
                            :available-option="isFoodBrand"
                            @removeSetting="removeWdayIndexRegularHour(i)"
                            @set-field-value="setFieldValue"
                          />
                        </section>
                        <KkAddLabel
                          :label="$t('pages.CompaniesLocationStore.businessHours.wdayIndexRegularHours.addSetting')"
                          @click="addWdayIndexRegularHours"
                        />
                      </div>
                    </div>
                  </div>
                </template>
              </KkBox>
              <KkBox v-if="!isDocomoPlan" :title="$t('pages.CompaniesLocationStore.businessHours.additionalExplanation.title')" border class="section">
                <template #content>
                  <div>
                    <ValidationProvider key="storeBusinessHoursRegularHolidayAdditionalDescription" v-slot="{ errors, value, handleChange }" v-model="regularHolidayAdditionalDescription.context" name="定休日の補足" rules="max:60">
                      <label class="c-formLabel">{{ $t('pages.CompaniesLocationStore.businessHours.additionalExplanation.regularHoliday') }}({{ $t('labels.countCharacters', {count: regularHolidayAdditionalDescription.context.length, max: 60}) }}){{ $t('labels.optionalLabel') }}</label>
                      <KkTextarea :model-value="value" placeholder="" @update:model-value="handleChange" />
                      <ValidationError :error="errors[0]" />
                    </ValidationProvider>
                  </div>
                  <div class="additionalDescription">
                    <ValidationProvider key="storeBusinessHoursRegularHourAdditionalDescription" v-slot="{ errors, value, handleChange }" v-model="storeRegularHourAdditionalDescription.context" name="営業時間の補足" rules="max:120">
                      <label class="c-formLabel">{{ $t('pages.CompaniesLocationStore.businessHours.additionalExplanation.businessHours') }}({{ $t('labels.countCharacters', {count: storeRegularHourAdditionalDescription.context.length, max: 120}) }}){{ $t('labels.optionalLabel') }}</label>
                      <KkTextarea :model-value="value" placeholder="" @update:model-value="handleChange" />
                      <ValidationError :error="errors[0]" />
                    </ValidationProvider>
                  </div>
                </template>
              </KkBox>
              <KkBox v-if="!isDocomoPlan" :title="$t('pages.CompaniesLocationStore.businessHours.moreHours.title')" border class="section">
                <template #content>
                  <template v-if="storeBusinessHoursMeta.moreHoursTypeApiError">
                    <KkInfo icon="exclamation" alert>
                      {{ $t('models.moreHour.moreHoursTypeApiError.info') }}
                    </KkInfo>
                  </template>
                  <template v-else>
                    <KkInfo>
                      {{ $t('pages.CompaniesLocationStore.businessHours.moreHours.info') }}
                    </KkInfo>
                    <KkCheckbox
                      v-show="!selectedMoreHour"
                      id="showMoreHoursForm"
                      :model-value="showMoreHoursForm"
                      :label="$t('pages.CompaniesLocationStore.businessHours.moreHours.set')"
                      class="showMoreHoursFormCheckbox"
                      @update:modelValue="handleCheckMoreHour"
                    />
                    <template v-if="showMoreHoursForm">
                      <div v-if="selectedMoreHour">
                        <div class="moreHourName">
                          <p class="moreHourName__title">{{ $t('pages.CompaniesLocationStore.businessHours.name') }}</p>
                          <h4 class="moreHourName__name">
                            {{ displayMoreHoursTypeName(selectedMoreHour.gmbHoursTypeId) }}
                          </h4>
                        </div>
                        <div>
                          <div class="c-grid">
                            <div class="c-grid__items">
                              <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__10 c-grid__item__l__10">
                                <section v-for="(moreHour, i) in selectedMoreHour.weeks" :key="'moreHour' + i" class="p-box">
                                  <RegularHourCard hour-type="regular" :regular-hour="moreHour" :index="'moreHour' + i" more-hour :available-option="isFoodBrand" @set-field-value="setFieldValue" />
                                </section>
                              </div>
                            </div>
                          </div>
                          <KkInfo class="qualityCheckDescriptionInfo">
                            {{ $t('messages.qualityCheckDescription') }}
                          </KkInfo>
                          <div class="buttonGroup">
                            <div class="buttonGroup__item">
                              <KkButton :text="$t('labels.cancel')" color="secondary" @click="handleClickEditCancel" />
                            </div>
                            <div class="buttonGroup__item">
                              <KkButton :text="$t('labels.confirm')" @click="handleClickEditDetermine" />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else class="moreHourContent">
                        <KkBoxWithMenu
                          v-for="moreHour in displayMoreHours"
                          :key="`registeredMoreHour_${moreHour.gmbHoursTypeId}`"
                          :menu-list="menuList"
                          class="moreHourContent__menu"
                          @on-click-edit-menu="e => handleClickEditMenu(e, moreHour.gmbHoursTypeId)"
                        >
                          <div>
                            <h5 class="moreHourContent__name">{{ displayMoreHoursTypeName(moreHour.gmbHoursTypeId) }}</h5>
                            <template v-for="week in moreHour.weeks" :key="`registeredMoreHour_${week.wday}`">
                              <div class="moreHourContentWdayTime">
                                <p class="moreHourContentWdayTime__wday">
                                  {{ $tm(`labels.weekday.${week.wday}`) }}
                                </p>
                                <p>
                                  {{ displayHour('moreHour')(week) }}
                                </p>
                              </div>
                            </template>
                          </div>
                        </KkBoxWithMenu>
                        <div class="addCards">
                          <KkAddCard v-for="moreHour in notRegisteredMoreHours"
                                     :key="'notRegisteredMoreHour_' + moreHour.gmbHoursTypeId"
                                     :name="displayMoreHoursTypeName(moreHour.gmbHoursTypeId)"
                                     class="addCardWrapper"
                                     @click="handleClickMoreHour(moreHour.gmbHoursTypeId)"
                          />
                        </div>
                      </div>
                    </template>
                  </template>
                </template>
              </KkBox>
              <section v-if="!isDocomoPlan" class="p-box">
                <header class="p-box__header">
                  <h3 class="p-box__title">{{ $t('pages.CompaniesLocationStore.businessHours.specialHours.label') }}</h3>
                </header>
                <div class="p-box__body">
                  <div class="c-info">
                    <div class="c-info__icon">
                      <Icon name="iconQuestion" />
                    </div>
                    <div class="c-info__content">
                      <p>{{ $t('pages.CompaniesLocationStore.businessHours.specialHours.info1') }}</p>
                      <p>{{ $t('pages.CompaniesLocationStore.businessHours.specialHours.info2') }}</p>
                      <p>{{ $t('pages.CompaniesLocationStore.businessHours.specialHours.info3') }}</p>
                    </div>
                  </div>
                  <template v-if="showSpecialHoursForm">
                    <fieldset class="p-formGrid__item p-formGrid__item__s__12 p-formGrid__item__m__12 p-formGrid__item__l__12">
                      <input
                        id="checkbox_special_hours"
                        :checked="storeSpecialHours.length !== 0"
                        class="c-checkbox__input"
                        type="checkbox"
                        @change="toggleSetSpecialHours"
                      >
                      <label class="c-checkbox__label" for="checkbox_special_hours">{{ $t('pages.CompaniesLocationStore.businessHours.specialHours.label') }}</label>
                    </fieldset>
                    <div class="c-grid">
                      <div class="c-grid__items">
                        <div class="c-grid__item c-grid__item__s__12 c-grid__item__m__12 c-grid__item__l__12">
                          <section v-for="futureSpecialHour in futureSpecialHours" :key="futureSpecialHour['specialHourUId']" class="p-box">
                            <SpecialHourCard :special-hour="futureSpecialHour" :i="specialHourIndex(futureSpecialHour['specialHourUId'])" :display-delete-btn="futureSpecialHours.length > 1" @remove-special-hour="removeStoreSpecialHour" />
                          </section>
                          <div v-if="pastSpecialHours.length">
                            <span
                              class="showSpecialHoursLink"
                              @click="showPastSpecialHours = !showPastSpecialHours"
                            >
                              {{ $t('pages.CompaniesLocationStore.businessHours.specialHours.showPast') }}
                            </span>
                          </div>
                          <div v-if="showPastSpecialHours">
                            <section v-for="pastSpecialHour in pastSpecialHours" :key="pastSpecialHour['specialHourUId']" class="p-box">
                              <SpecialHourCard :special-hour="pastSpecialHour" :i="specialHourIndex(pastSpecialHour['specialHourUId'])" :display-delete-btn="pastSpecialHours.length > 1" @remove-special-hour="removeStoreSpecialHour" />
                            </section>
                          </div>
                          <div class="specialHourAdd c-button c-button--icon">
                            <div class="c-button__item c-button__item--primary" @click="addSpecialHour">
                              <img :src="ImgIconPlusRed" alt="" class="c-button__icon">
                              <span>{{ $t('labels.add', { value: $t('pages.CompaniesLocationStore.businessHours.specialHours.elseDate') }) }}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                </div>
              </section>
              <GmbProtection
                v-if="hasGmbLocation"
                v-model="gmbLocationSynced.business_hours_synced"
                :title="$t('pages.CompaniesLocationStore.gmbProtection.edit.title', { value: $t('models.storeRegularHour.modelName') })"
              />
            </template>
          </div>
          <footer v-show="dataLoaded" class="p-box__footer">
            <div class="c-button c-button--right">
              <router-link :to="{ name: 'CompaniesLocationStore', params: { company_uuid: companyUuid, store_uuid: storeUuid }}" class="c-button__item c-button__item--outline-cancel">
                {{ $t('labels.cancel') }}
              </router-link>
              <button
                :disabled="selectedMoreHour"
                :class="{'c-button__item--disabled': selectedMoreHour}"
                class="c-button__item c-button__item--primary"
                @click.prevent="openModal"
              >
                {{ $t('labels.save') }}
              </button>
            </div>
          </footer>
        </form>
      </ValidationObserver>
    </section>
    <KkPreviewModal
      v-if="showPreview"
      :is-visible="showPreview"
      :business-hour-synced="gmbLocationSynced.business_hours_synced"
      :title="$t('models.businessHours.previewTitle')"
      :linked-sites="linkedSites"
      :business-hour-request-body="businessHourRequestBody"
      :enabled-business-hours-crawler="enabledBusinessHoursCrawler"
      @close="showPreview=false"
      @submit="onSubmit"
    />
  </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep'
import moment from 'moment'
import { mapState, mapActions, mapGetters } from 'vuex'
import Loading from '../components/Loading.vue'
import Icon from '../components/Icon'
import Info from '../components/Info'
import ValidationError from '../components/ValidationError'
import GmbProtection from '../components/CompaniesLocationStore/GmbProtection.vue'
import iconCrossPath from '../../../images/common/icon_cross.svg'
import ImgIconPlusRed from '../../../images/common/icon_plus_red.svg'
import RegularHourCard from '../components/RegularHourCard'
import SpecialHourCard from '../components/SpecialHourCard'
import { basicDateFormat } from '../modules/date'
import { KkAddLabel, KkInfo } from '../components/ui'
import { KkAddCard, KkBox, KkBoxWithMenu, KkCheckbox, KkButton, KkTextarea } from '../components/base'
import { displayHour } from '../modules/regularHour'
import generateRandomStr from '../modules/generateRandomStr'
import { KkWdayIndexRegularHourCard, KkPreviewModal } from '../components/domains/location/store/businessHour'

export default {
  name: 'CompaniesLocationStoreRegularHoursEdit',
  components: {
    KkButton,
    RegularHourCard,
    Loading,
    Icon,
    Info,
    ValidationError,
    GmbProtection,
    SpecialHourCard,
    KkInfo,
    KkBox,
    KkAddCard,
    KkCheckbox,
    KkBoxWithMenu,
    KkTextarea,
    KkWdayIndexRegularHourCard,
    KkPreviewModal,
    KkAddLabel
  },
  beforeRouteLeave(to, from, next) {
    if (this.confirmationFlg) {
      confirm(this.$t('pages.CompaniesLocationStore.businessHours.moreHours.confirm'))
        ? next()
        : next(false)
      return
    }
    next()
  },
  emits: ['update-title'],
  data () {
    return {
      iconCrossPath,
      ImgIconPlusRed,
      selectedMoreHour: undefined,
      showMoreHoursForm: false,
      confirmationFlg: false,
      showPastSpecialHours: false,
      showPreview: false
    }
  },
  computed: {
    ...mapState({
      storeRegularHours: state => state.companiesStoreBusinessHours.regularHours,
      storeHolidayRegularHour: state => state.companiesStoreBusinessHours.holidayRegularHour,
      storeDayBeforeHolidayRegularHour: state => state.companiesStoreBusinessHours.dayBeforeHolidayRegularHour,
      storeRegularHourAdditionalDescription: state => state.companiesStoreBusinessHours.regularHourAdditionalDescription,
      regularHolidayAdditionalDescription: state => state.companiesStoreBusinessHours.regularHolidayAdditionalDescription,
      storeSpecialHours: state => state.companiesStoreBusinessHours.specialHours,
      storeMoreHours: state => state.companiesStoreBusinessHours.moreHours,
      storeRegularHoursLoading: state => state.companiesStoreBusinessHours.loading,
      storeBusinessHoursMeta: state => state.companiesStoreBusinessHours.meta,
      storeBusinessHoursCrawlers: state => state.companiesStoreBusinessHoursCrawlers.data,
      storeBusinessHoursCrawlersLoading: state => state.companiesStoreBusinessHoursCrawlers.loading,
      store: state => state.companiesLocationStore.data,
      storeLoading: state => state.companiesLocationStore.loading,
      storeGmb: state => state.companiesStoreGmb.data,
      storeGmbConnected: state => state.companiesStoreGmb.connected,
      storeGmbLoading: state => state.companiesStoreGmb.loading,
      storeExternalAccounts: state => state.companiesStoreExternalAccounts.data,
      storeExternalAccountsLoading: state => state.companiesStoreExternalAccounts.loading,
      allGmbStores: (state) => state.companiesGmbStores.data ? state.companiesGmbStores.data.data : [], // 全ての（全ページの）GMB連携済の店舗
    }),
    ...mapGetters({
      syncedAll: 'companiesStoreGmb/syncedAll',
      gmbLocationSynced: 'companiesStoreGmb/gmbLocationSynced',
      hasGmbLocation: 'companiesStoreGmb/hasGmbLocation',
      displayMoreHours: 'companiesStoreBusinessHours/displayMoreHours',
      notRegisteredMoreHours: 'companiesStoreBusinessHours/notRegisteredMoreHours',
      providedFeaturesBusinessHoursCrawler: 'companiesCompany/providedFeaturesBusinessHoursCrawler',
      providedFeaturesExternalAccount: 'companiesCompany/providedFeaturesExternalAccount',
      futureSpecialHours: 'companiesStoreBusinessHours/futureSpecialHours',
      pastSpecialHours: 'companiesStoreBusinessHours/pastSpecialHours',
      storeWdayIndexRegularHours: 'companiesStoreBusinessHours/wdayIndexRegularHours',
      isDocomoPlan: 'companiesCompany/isDocomoPlan',
    }),
    companyUuid() {
      return this.$route.params.company_uuid
    },
    storeUuid() {
      return this.$route.params.store_uuid
    },
    isAllRegularHoliday() {
      return this.storeRegularHours.every(hour => this.isRegularHoliday(hour))
    },
    loading() {
      return this.storeRegularHoursLoading || this.storeGmbLoading || this.storeBusinessHoursCrawlersLoading || this.storeExternalAccountsLoading || this.storeLoading
    },
    dataLoaded() {
      return this.gmbLocationSynced && !this.loading
    },
    showSpecialHoursForm() {
      return !this.isAllRegularHoliday
    },
    enabledBusinessHoursCrawler() {
      return this.storeBusinessHoursCrawlers?.enabled
    },
    menuList() {
      return [
        {
          type: 'edit',
          text: this.$t('labels.edit')
        },
        {
          type: 'delete',
          text: this.$t('labels.delete')
        },
      ]
    },
    linkedSites() {
      const sites = this.storeGmbConnected ? ['google'] : []
      if(this.store.attributes.docomo_connected) sites.push('docomo')
      const externalAccountSites = this.storeExternalAccounts?.map(account => account.attributes.service_type)
        // TODO:: バックエンドからyahooPlaceを返してもらったら外す
        .map(v => v === 'yahooLoco' ? 'yahooPlace' : v)
      return sites.concat(externalAccountSites).filter(v => v)
    },
    businessHourRequestBody() {
      return {
        companyUuid: this.companyUuid,
        storeUuid: this.storeUuid,
        regularHours: this.storeRegularHours,
        holidayRegularHour: this.storeHolidayRegularHour,
        dayBeforeHolidayRegularHour: this.storeDayBeforeHolidayRegularHour,
        wdayIndexRegularHours: this.storeWdayIndexRegularHours,
        regularHourAdditionalDescription: this.storeRegularHourAdditionalDescription,
        regularHolidayAdditionalDescription: this.regularHolidayAdditionalDescription,
        specialHours:  this.storeSpecialHours,
        moreHours: this.storeMoreHours,
        synced: this.gmbLocationSynced.business_hours_synced,
      }
    },
    isFoodBrand() {
      const result = this.allGmbStores.find(v => this.storeUuid === v.attributes.uuid)
      if (!result) return false
      return result.attributes.food_brand
    }
  },
  watch: {
    storeRegularHours: {
      handler(hours) {
        if (this.$refs.form) this.$refs.form.validate()
        if (hours.every(hour => this.isRegularHoliday(hour))) {
          this.removeAllStoreSpecialHour()
        }
      },
      deep: true
    }
  },
  async created() {
    const actions = [
      this.readStore({ company_uuid: this.companyUuid, store_uuid: this.storeUuid }),
      this.providedFeaturesBusinessHoursCrawler ? this.readStoreBusinessHoursCrawlers({ company_uuid: this.companyUuid, store_uuid: this.storeUuid }) : Promise.resolve(),
      this.readStoreBusinessHours({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })
    ]
    if(!this.isDocomoPlan) {
      this.readStoreGmb({ company_uuid: this.companyUuid, store_uuid: this.storeUuid })
      if (this.providedFeaturesExternalAccount) {
        actions.push(this.readExternalAccounts({ company_uuid: this.companyUuid, store_uuid: this.storeUuid }))
      }
      actions.push(this.readGmbStores(this.companyUuid))
    }
    await Promise.all(actions)
    if (!this.storeHolidayRegularHour) this.setInitialData('holiday')
    if (!this.storeDayBeforeHolidayRegularHour) this.setInitialData('dayBeforeHoliday')
    this.showMoreHoursForm = this.storeMoreHours.length !== 0
    this.$emit(
      'update-title',
      this.$t('pages.CompaniesLocationStoreRegularHoursEdit.title', {
        name: this.store.attributes.name
      })
    )
  },
  methods: {
    ...mapActions({
      readStore: 'companiesLocationStore/readStore',
      addStoreMoreHour: 'companiesStoreBusinessHours/addStoreMoreHour',
      removeStoreMoreHour: 'companiesStoreBusinessHours/removeStoreMoreHour',
      removeAllStoreMoreHour: 'companiesStoreBusinessHours/removeAllStoreMoreHour',
      addStoreSpecialHour: 'companiesStoreBusinessHours/addStoreSpecialHour',
      removeStoreSpecialHour: 'companiesStoreBusinessHours/removeStoreSpecialHour',
      removeAllStoreSpecialHour: 'companiesStoreBusinessHours/removeAllStoreSpecialHour',
      readStoreBusinessHours: 'companiesStoreBusinessHours/readStoreBusinessHours',
      updateStoreBusinessHours: 'companiesStoreBusinessHours/updateStoreBusinessHours',
      addWdayIndexRegularHours: 'companiesStoreBusinessHours/addWdayIndexRegularHours',
      removeWdayIndexRegularHour: 'companiesStoreBusinessHours/removeWdayIndexRegularHour',
      readStoreBusinessHoursCrawlers: 'companiesStoreBusinessHoursCrawlers/readStoreBusinessHoursCrawlers',
      updateStoreBusinessHoursCrawlers: 'companiesStoreBusinessHoursCrawlers/updateStoreBusinessHoursCrawlers',
      setInitialData: 'companiesStoreBusinessHours/setInitialData',
      readStoreGmb: 'companiesStoreGmb/readStoreGmb',
      readExternalAccounts: 'companiesStoreExternalAccounts/readExternalAccounts',
      readGmbStores: 'companiesGmbStores/readGmbStores',
    }),
    handleCheckMoreHour() {
      this.removeAllStoreMoreHour()
      this.showMoreHoursForm = !this.showMoreHoursForm
      this.confirmationFlg = true
    },
    displayHour,
    displayMoreHoursTypeName(gmbHoursTypeId) {
      return this.storeBusinessHoursMeta.moreHoursTypes.find(v => v.gmbHoursTypeId === gmbHoursTypeId).localizedDisplayName
    },
    handleClickEditMenu(type, id) {
      switch (type) {
      case 'edit':
        this.handleClickMoreHour(id)
        break
      case 'delete':
        this.handleDeleteMoreHour(id)
      }
    },
    handleDeleteMoreHour(id) {
      this.removeStoreMoreHour(id)
      this.selectedMoreHour = undefined
      this.confirmationFlg = true
    },
    handleClickEditCancel() {
      this.selectedMoreHour = undefined
    },
    handleClickEditDetermine() {
      this.addStoreMoreHour(this.selectedMoreHour)
      this.selectedMoreHour = undefined
      this.confirmationFlg = true
    },
    handleClickMoreHour(id) {
      this.selectedMoreHour = cloneDeep(this.storeMoreHours.find(v => v.gmbHoursTypeId === id)) || this.buildEmptyMoreHour(id)
    },
    buildEmptyMoreHour(gmbHoursTypeId) {
      return {
        gmbHoursTypeId: gmbHoursTypeId,
        weeks: [
          { wday: 'sunday', periods: [] },
          { wday: 'monday', periods: [] },
          { wday: 'tuesday', periods: [] },
          { wday: 'wednesday', periods: [] },
          { wday: 'thursday', periods: [] },
          { wday: 'friday', periods: [] },
          { wday: 'saturday', periods: [] },
        ]
      }
    },
    isRegularHoliday(hour) {
      return hour.periods.length === 0
    },
    toggleSetSpecialHours() {
      this.storeSpecialHours.length === 0 ? this.addSpecialHour() : this.removeAllStoreSpecialHour()
    },
    addSpecialHour() {
      this.addStoreSpecialHour({ date: basicDateFormat(moment()), periods: [], specialHourUId: `future-${generateRandomStr(1000)}` })
    },
    async onSubmit() {
      const result = await this.$refs.form.validate()
      if (!result.valid) {
        alert(this.$t('messages.invalid'))
        return
      }
      if(!this.businessHourRequestBody.regularHolidayAdditionalDescription.context || !this.businessHourRequestBody.regularHourAdditionalDescription.context) {
        if(!confirm(this.$t('pages.CompaniesLocationStore.businessHours.additionalExplanation.confirm'))) return
      }
      await this.updateStoreBusinessHours(this.businessHourRequestBody)
      if (this.providedFeaturesBusinessHoursCrawler) {
        await this.updateStoreBusinessHoursCrawlers(
          {
            company_uuid: this.companyUuid,
            store_uuid: this.storeUuid,
            payload: {
              business_hours_crawler: {
                enabled: this.storeBusinessHoursCrawlers.enabled,
                url: this.storeBusinessHoursCrawlers.url
              }
            }
          }
        )
      }
      this.confirmationFlg = false
      this.closeModal()
    },
    specialHourIndex(_specialHourUId) {
      return this.storeSpecialHours
        .map(v => v.specialHourUId)
        .indexOf(_specialHourUId)
    },
    async openModal() {
      const result = await this.$refs.form.validate()
      if (!result.valid) {
        alert(this.$t('messages.invalid'))
        return
      }
      this.showPreview = true
    },
    closeModal() {
      this.showPreview = false
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../../stylesheets/biz_spa/foundation/_var.scss';
.c-info {
  &__content {
    white-space: pre-line;
  }
}
.section {
  margin-top: 32px;
}
.addCards {
  margin-top: 16px;
}
.addCardWrapper {
  margin-bottom: 8px;
}
.moreHourContent {
  margin-top: 16px;
  width: 600px;
  &__name {
    margin-bottom: 16px;
    font-size: 16px;
  }
  &__menu {
    margin-bottom: 8px;
  }
}
.moreHourContentWdayTime {
  display: flex;
  &__wday {
    margin-right: 32px;
  }
}
.showMoreHoursFormCheckbox {
  margin-top: 16px;
}
.moreHourName {
  margin: 16px 0;
  &__title {
    font-weight: bold;
  }
  &__name {
    font-size: 24px;
  }
}
.qualityCheckDescriptionInfo {
  margin-top: 24px;
}
.buttonGroup {
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  &__item {
    margin-right: 8px;
  }
}
.showSpecialHoursLink {
  color: $color-accent-primary;
  text-decoration: underline;
  cursor: pointer;
}
.qualityCheckDescription {
  margin-bottom: 16px;
}

.additionalDescription {
  margin-top: 32px;
}
</style>
